import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ChevronLeft, RotateCcw, Send, Siren } from 'lucide-react';
import { getNotices, preview, publish } from '@/lib/api';
import {
  formatFull,
  formatNumber,
  isoAtNextHour,
  isoInHours,
  toIsoFromLocalInput,
  toLocalInput,
} from '@/lib/format';
import type { Draft, FieldError, Preview, Session } from '@/lib/types';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { NoticePreview, PageTitle, PushPreview } from '@/components/shell';
import { CRITICAL_ALERT_ENABLED, LINK_ENABLED } from '@/lib/features';
import { clearDraft, loadDraft, saveDraft } from '@/lib/draft';
import { cn } from '@/lib/utils';

type Preset = 'sera' | 'domani' | 'h24' | 'h48' | 'custom';

const PRESET_ISO: Record<Exclude<Preset, 'custom'>, () => string> = {
  sera: () => isoAtNextHour(20),
  domani: () => isoAtNextHour(8),
  h24: () => isoInHours(24),
  h48: () => isoInHours(48),
};

/**
 * Composizione e conferma.
 *
 * Due passi, **due schermate** e non un dialog: la conferma deve avere il peso
 * di un passo, non di un «sei sicuro?» che si clicca senza leggere.
 *
 * La validazione non è duplicata qui. La console manda la bozza a
 * `preview_comune_notice()` e mostra gli errori che tornano, sul campo che li
 * ha causati. Una console che pre-valida per essere gentile finisce col
 * pre-validare in modo diverso dal server, e allora l'anteprima mente — che è
 * esattamente ciò che questa schermata esiste per non fare.
 */
export function Nuovo({ session }: { session: Session }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const revocaId = params.get('revoca');

  const notices = useQuery({
    queryKey: ['notices', session.istat],
    queryFn: () => getNotices(session.istat, 50),
  });
  const attivi = useMemo(
    () => (notices.data ?? []).filter((n) => n.stato === 'attivo'),
    [notices.data],
  );

  // La bozza si riprende da dov'era: vedi lib/draft.ts. Se la scadenza salvata
  // è ormai passata — succede appena la bozza sopravvive qualche ora — si
  // ricalcola dal preset, invece di far arrivare l'operatore alla conferma con
  // un errore che non ha commesso lui.
  const salvata = useMemo(() => loadDraft(session.istat), [session.istat]);

  const [draft, setDraft] = useState<Draft>(() => {
    const base: Draft = {
      title: '',
      body: '',
      linkUrl: '',
      linkLabel: '',
      expiresAt: isoInHours(session.limits.default_ttl_hours),
      push: true,
      critical: false,
      revokesId: revocaId,
    };
    if (!salvata) return base;
    const scaduta = Date.parse(salvata.draft.expiresAt) <= Date.now();
    return {
      ...base,
      ...salvata.draft,
      expiresAt: scaduta ? base.expiresAt : salvata.draft.expiresAt,
      // Una bozza critica salvata prima di spegnere il box non deve partire
      // critica da un box che non c'è più. Le bozze vecchie senza il campo
      // ereditano il `false` di `base`.
      critical: CRITICAL_ALERT_ENABLED && salvata.draft.critical === true,
      // Una revoca chiesta adesso dall'URL vince sulla bozza vecchia.
      revokesId: revocaId ?? salvata.draft.revokesId,
    };
  });
  const [preset, setPreset] = useState<Preset>(
    () => ((salvata?.preset as Preset) ?? 'h24'),
  );
  const [ripresa, setRipresa] = useState(() => Boolean(salvata));

  /**
   * La chiave di questo tentativo di pubblicazione.
   *
   * Nasce con la bozza e vive quanto lei: si riprende da `sessionStorage` se
   * c'è, perché il caso da coprire è proprio quello in cui la scheda è stata
   * ricaricata mentre la publish era in volo. Muore con `clearDraft`, cioè a
   * pubblicazione riuscita — il prossimo avviso è un avviso nuovo e vuole una
   * chiave nuova.
   *
   * Conseguenza da conoscere: se la risposta si perde, l'operatore ricarica,
   * CAMBIA il testo e ripubblica, il server restituisce l'avviso di prima e la
   * modifica non parte. È l'esito giusto — l'alternativa è una seconda notifica
   * a tutto il Comune per un avviso già mandato — e la strada per correggerlo è
   * quella di sempre: si ritira e si ripubblica.
   */
  const [tentativo] = useState<string>(() => salvata?.tentativo ?? crypto.randomUUID());
  const [errors, setErrors] = useState<FieldError[]>([]);
  const [step, setStep] = useState<'compose' | 'confirm'>('compose');
  const [checked, setChecked] = useState<Preview | null>(null);

  // I due passi sono uno stato, non due rotte: lo scroll non si riavvolge da
  // solo. Senza questo si clicca «Continua» in fondo alla form e si atterra a
  // metà della conferma — cioè sotto il numero di destinatari, che è l'unica
  // cosa che quella schermata esiste per far vedere.
  //
  // Graffe obbligatorie, per la ragione scritta in `components/Layout.tsx`.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  // Salvataggio continuo. Costa una scrittura in sessionStorage per tasto
  // premuto — nulla — e vale il non dover mai ridigitare un avviso.
  useEffect(() => {
    saveDraft(session.istat, draft, preset, tentativo);
  }, [session.istat, draft, preset, tentativo]);

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const errorFor = (field: string) => errors.find((e) => e.field === field)?.message;

  const check = useMutation({
    mutationFn: () => preview(session.istat, draft),
    onSuccess: (p) => {
      setErrors(p.errors);
      setChecked(p);
      if (p.ok) setStep('confirm');
    },
  });

  const send = useMutation({
    mutationFn: () => publish(session.istat, draft, tentativo),
    onSuccess: (res) => {
      if (!res.ok) {
        // Rivalidato lato server: fra l'anteprima e il click possono essere
        // passati minuti, e quota o fascia oraria possono essere cambiate.
        setErrors(res.errors ?? []);
        setStep('compose');
        return;
      }
      // Pubblicato: la bozza ha finito il suo lavoro. Si cancella solo qui,
      // mai su un errore — un fallimento non deve costare quello che c'è
      // scritto.
      clearDraft(session.istat);
      void qc.invalidateQueries({ queryKey: ['notices', session.istat] });
      void qc.invalidateQueries({ queryKey: ['municipal-session'] });
      navigate(res.id ? `/avviso/${res.id}` : '/');
    },
  });

  /* ------------------------------------------------------------------ */
  /* Passo 2 — conferma                                                  */
  /* ------------------------------------------------------------------ */
  if (step === 'confirm' && checked) {
    /* DISPOSITIVI, non persone: chi ha due telefoni riceve due notifiche, e
       chi non ha un account non ha un utente da contare. */
    const dispositivi = checked.recipients;
    const seguono = checked.recipients_total ?? dispositivi;
    const notifying = checked.push !== null && dispositivi > 0;
    /* I due numeri divergono finché il worker consegna solo alla flotta
       interna. Mostrare solo quello grande prometterebbe una platea che non
       arriverà; solo quello piccolo farebbe credere al Comune di avere tre
       follower. Si dice la differenza, dove si vede. */
    const parziale = seguono > dispositivi;
    /* La criticità è una scelta della bozza, non un dato del server: l'RPC
       la riceve come `urgency` ma non la restituisce nell'anteprima. Si
       ripete QUI, con lo stesso peso del numero di destinatari, perché è la
       seconda cosa irreversibile di questa schermata. */
    const critica = notifying && draft.critical;
    const unita = dispositivi === 1 ? 'dispositivo' : 'dispositivi';

    return (
      <div className="space-y-6">
        <PageTitle
          above={
            <Button variant="ghost" size="sm" onClick={() => setStep('compose')}>
              <ChevronLeft />
              Modifica
            </Button>
          }
        >
          {critica ? (
            <>
              Stai per inviare una{' '}
              <span className="text-rossa">notifica critica</span> a{' '}
              <span className="tnum text-rossa">{formatNumber(dispositivi)}</span>{' '}
              {unita}
            </>
          ) : notifying ? (
            <>
              Stai per notificare{' '}
              <span className="tnum text-primary">{formatNumber(dispositivi)}</span>{' '}
              {unita}
            </>
          ) : (
            'Pubblichi senza notificare'
          )}
        </PageTitle>

        <p className="-mt-4 text-[16px] leading-relaxed text-ink2">
          {notifying
            ? 'Una notifica non si annulla e non si corregge. Rileggi: è quello che comparirà sui telefoni.'
            : 'L’avviso comparirà in app a chi segue il Comune. Nessuno riceverà una notifica.'}
        </p>

        {critica && (
          <Alert className="border-rossa/50 bg-rossa/10 text-ink">
            <Siren className="text-rossa" />
            <AlertTitle className="text-rossa">Questa è una notifica critica</AlertTitle>
            <AlertDescription className="text-ink2">
              {/* Un solo figlio: la descrizione è una griglia, e un bottone
                  nudo in mezzo al testo finirebbe su una riga sua. */}
              <p>
                Suonerà a volume pieno anche sui telefoni in silenzioso o con
                «Non disturbare» attivo. Se non c’è un pericolo immediato per
                le persone, torna a{' '}
                <button
                  type="button"
                  className="font-medium underline"
                  onClick={() => setStep('compose')}
                >
                  Modifica
                </button>{' '}
                e spegnila.
              </p>
            </AlertDescription>
          </Alert>
        )}

        {/* La differenza fra chi segue e chi riceverà si dice QUI, prima di
            premere, e non nelle statistiche del giorno dopo. */}
        {notifying && parziale && (
          <p className="-mt-4 text-[15px] leading-relaxed text-ink3">
            Il Comune è seguito da{' '}
            <span className="tnum">{formatNumber(seguono)}</span> dispositivi, ma
            l’invio è ancora limitato alla flotta di prova: la notifica arriverà a{' '}
            <span className="tnum">{formatNumber(dispositivi)}</span>.
          </p>
        )}

        {checked.push && (
          <Card>
            <CardHeader>
              <CardTitle>Sulla schermata di blocco</CardTitle>
            </CardHeader>
            <CardContent>
              <PushPreview title={checked.push.title} body={checked.push.body} />
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Nell’app</CardTitle>
            <CardDescription>
              La scheda del Comune, per chi lo segue.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <NoticePreview card={checked.card} />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Dopo l’invio</CardTitle>
            {checked.push && !checked.push.costs_quota && (
              <CardDescription>
                Questa è una revoca: non consuma budget.
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="flex gap-8">
            <p className="tnum text-[15px] text-ink2">
              Oggi{' '}
              <strong className="text-ink">
                {checked.quota_after.daily_used}/{checked.quota_after.daily_cap}
              </strong>
            </p>
            <p className="tnum text-[15px] text-ink2">
              Settimana{' '}
              <strong className="text-ink">
                {checked.quota_after.weekly_used}/{checked.quota_after.weekly_cap}
              </strong>
            </p>
          </CardContent>
        </Card>

        {send.isError && (
          <Alert variant="destructive">
            <AlertTriangle />
            <AlertTitle>Non sono riuscito a pubblicare</AlertTitle>
            <AlertDescription>
              Nulla è stato inviato. Riprova; se l’errore resta, l’avviso non è
              uscito e puoi usare gli altri canali del Comune.
            </AlertDescription>
          </Alert>
        )}

        {/* Un bottone solo. «Invia solo a me» stava qui accanto fino al
            14/08/2026: mandava ai device di chi cliccava, e un accesso creato
            dal pannello per la console non ne ha nessuno — diceva «Inviata solo
            a te» e non squillava niente. Una prova che non prova è peggio di
            nessuna prova, perché fa premere Pubblica con più sicurezza. */}
        <Button
          size="lg"
          variant={critica ? 'destructive' : 'default'}
          disabled={send.isPending}
          onClick={() => send.mutate()}
          className="w-full"
        >
          {critica ? <Siren /> : <Send />}
          {send.isPending
            ? 'Pubblico…'
            : critica
              ? `Invia la notifica critica a ${formatNumber(dispositivi)} ${unita}`
              : notifying
                ? `Pubblica e notifica ${formatNumber(dispositivi)} ${unita}`
                : 'Pubblica senza notificare'}
        </Button>
      </div>
    );
  }

  /* ------------------------------------------------------------------ */
  /* Passo 1 — composizione                                              */
  /* ------------------------------------------------------------------ */
  const formError = errorFor('form');
  const pushError = errorFor('push_requested');

  return (
    <form
      className="space-y-6"
      onSubmit={(e) => {
        e.preventDefault();
        check.mutate();
      }}
    >
      <PageTitle
        above={
          <Button asChild variant="ghost" size="sm">
            <Link to="/">
              <ChevronLeft />
              Home
            </Link>
          </Button>
        }
      >
        Nuovo avviso
      </PageTitle>

      {ripresa && (
        <Alert className="border-verde/40 bg-verde/10">
          <RotateCcw className="text-verde" />
          <AlertTitle>Ho ripreso quello che avevi scritto</AlertTitle>
          <AlertDescription>
            La bozza era rimasta da prima. Controlla la scadenza prima di
            continuare.{' '}
            <button
              type="button"
              className="font-medium underline"
              onClick={() => {
                clearDraft(session.istat);
                setRipresa(false);
                set('title', '');
                set('body', '');
              }}
            >
              Ricomincia da capo
            </button>
          </AlertDescription>
        </Alert>
      )}

      {formError && (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertDescription>{formError}</AlertDescription>
        </Alert>
      )}

      {/* Senza questo, una chiamata fallita non produceva NIENTE: il bottone
          tornava «Continua» e cliccarlo non faceva nulla, per sempre. Sembrava
          una console bloccata, e l'unico modo di sbloccarla era ricaricare —
          perdendo la bozza. */}
      {check.isError && (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>Non sono riuscito a controllare l’avviso</AlertTitle>
          <AlertDescription>
            {check.error instanceof Error
              ? check.error.message
              : 'Riprova fra un momento.'}{' '}
            Quello che hai scritto resta dov’è.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Contenuto</CardTitle>
          <CardDescription>
            Cosa sta facendo il Comune: le scuole, la strada, il punto di
            raccolta. Il livello di allerta lo dichiara la Regione, non tu.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <Field
            id="title"
            label="Titolo"
            error={errorFor('title')}
            counter={[draft.title.length, session.limits.title_max]}
          >
            <Input
              id="title"
              required
              autoFocus
              maxLength={session.limits.title_max}
              value={draft.title}
              aria-invalid={Boolean(errorFor('title')) || undefined}
              onChange={(e) => set('title', e.target.value)}
              placeholder="Scuole chiuse domani"
            />
          </Field>

          <Field
            id="body"
            label="Testo"
            hint="Quali scuole, quale tratto, fino a quando. Gli a capo si rispettano."
            error={errorFor('body')}
            counter={[draft.body.length, session.limits.body_max]}
          >
            <Textarea
              id="body"
              required
              rows={5}
              maxLength={session.limits.body_max}
              value={draft.body}
              aria-invalid={Boolean(errorFor('body')) || undefined}
              onChange={(e) => set('body', e.target.value)}
              placeholder="Tutte le scuole di ogni ordine e grado resteranno chiuse giovedì 14 agosto."
            />
          </Field>
        </CardContent>
      </Card>

      {/* Il link è spento: vedi lib/features.ts. La bozza continua a portarne
          i campi vuoti e il server ad accettarli, così riaccenderlo è una riga
          e non una migration. */}
      {LINK_ENABLED && (
        <Card>
          <CardHeader>
            <CardTitle>Approfondimento</CardTitle>
            <CardDescription>
              Facoltativo: l’ordinanza sul sito del Comune, la pagina della
              Protezione Civile locale.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-5 sm:grid-cols-2">
            <Field id="link_url" label="Indirizzo" error={errorFor('link_url')}>
              <Input
                id="link_url"
                type="url"
                inputMode="url"
                placeholder="https://comune.esempio.it/ordinanza-42"
                value={draft.linkUrl}
                aria-invalid={Boolean(errorFor('link_url')) || undefined}
                onChange={(e) => set('linkUrl', e.target.value)}
              />
            </Field>
            <Field
              id="link_label"
              label="Etichetta"
              hint="Vuoto = «Maggiori informazioni»."
            >
              <Input
                id="link_label"
                maxLength={30}
                placeholder="Leggi l’ordinanza"
                value={draft.linkLabel}
                onChange={(e) => set('linkLabel', e.target.value)}
              />
            </Field>
          </CardContent>
        </Card>
      )}

      {/* Solo la durata. L'interruttore «Emergenza» stava qui fino al
          14/08/2026 e prometteva una consegna diversa — «suona anche di notte,
          priorità piena» — che non è mai esistita: stessa push, stessa card.
          Il perché per esteso sta in `lib/types.ts`. */}
      <Card>
        <CardHeader>
          <CardTitle>Durata</CardTitle>
          <CardDescription>
            Ogni avviso ha una fine: oltre la scadenza sparisce dall’app da
            solo. Se serve più a lungo, si proroga dalla Home.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field
              id="preset"
              label="Valido fino a"
              error={errorFor('expires_at')}
              hint={
                <>
                  Scade {formatFull(draft.expiresAt)}. Massimo{' '}
                  <span className="tnum">{session.limits.max_ttl_hours}</span> ore.
                </>
              }
            >
              <Select
                value={preset}
                onValueChange={(v) => {
                  const p = v as Preset;
                  setPreset(p);
                  if (p !== 'custom') set('expiresAt', PRESET_ISO[p]());
                }}
              >
                <SelectTrigger id="preset" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sera">Stasera alle 20:00</SelectItem>
                  <SelectItem value="domani">Domani alle 8:00</SelectItem>
                  <SelectItem value="h24">Fra 24 ore</SelectItem>
                  <SelectItem value="h48">Fra 48 ore</SelectItem>
                  <SelectItem value="custom">Data e ora precise…</SelectItem>
                </SelectContent>
              </Select>
            </Field>

            {preset === 'custom' && (
              <Field id="expires_at" label="Data e ora">
                <Input
                  id="expires_at"
                  type="datetime-local"
                  value={toLocalInput(draft.expiresAt)}
                  aria-invalid={Boolean(errorFor('expires_at')) || undefined}
                  onChange={(e) => set('expiresAt', toIsoFromLocalInput(e.target.value))}
                />
              </Field>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Notifica</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <ToggleRow
            id="push"
            label="Notifica i cittadini"
            hint={
              draft.push
                ? draft.revokesId
                  ? 'Le revoche non consumano budget: sono i messaggi che vogliamo incoraggiare.'
                  : `Consuma 1 delle ${session.quota.daily_cap} push di oggi.`
                : 'L’avviso resta visibile in app e non sveglia nessuno.'
            }
            checked={draft.push}
            onChange={(v) => {
              set('push', v);
              // Senza push non c'è niente da rendere critico: l'interruttore
              // del box sotto si spegne insieme, invece di restare acceso su
              // una notifica che non parte.
              if (!v) set('critical', false);
            }}
          />

          {pushError && (
            <Alert className="border-arancione/50 bg-arancione/10">
              <AlertTriangle className="text-arancione" />
              <AlertDescription>{pushError}</AlertDescription>
            </Alert>
          )}

          {attivi.length > 0 && (
            <Field
              id="revoca"
              label="Chiude un avviso precedente"
              error={errorFor('revokes_id')}
              hint="L’avviso scelto scade subito e sparisce dall’app. Resta agli atti."
            >
              <Select
                value={draft.revokesId ?? 'none'}
                onValueChange={(v) => set('revokesId', v === 'none' ? null : v)}
              >
                <SelectTrigger id="revoca" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No, è un avviso nuovo</SelectItem>
                  {attivi.map((n) => (
                    <SelectItem key={n.id} value={n.id}>
                      {n.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
        </CardContent>
      </Card>

      {/* Box SEPARATO, e non una riga in più sotto «Notifica i cittadini»:
          questa scelta deve costare uno sguardo in più, non un tap in più.
          Cosa parte davvero oggi e cosa manca sta scritto in lib/features.ts,
          non qui: qui si parla all'operatore. */}
      {CRITICAL_ALERT_ENABLED && (
        <Card className="border-rossa/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-rossa">
              <Siren className="size-5" aria-hidden />
              Notifica critica
            </CardTitle>
            <CardDescription className="text-ink2">
              Su iPhone è una <em>critical alert</em> di iOS: suona a volume
              pieno anche con la suoneria spenta, con «Non disturbare» o una
              Full Immersion attiva. Passa sopra a tutto quello che chi la
              riceve ha impostato per non essere disturbato.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <Alert className="border-rossa/50 bg-rossa/10 text-ink">
              <AlertTriangle className="text-rossa" />
              <AlertTitle className="text-rossa">Usala con la massima attenzione</AlertTitle>
              <AlertDescription className="text-ink2">
                È riservata ai casi in cui c’è un pericolo immediato per le
                persone: evacuazione, allontanamento da un’area, acqua già in
                rete da non bere. Scuole chiuse, strade interrotte e avvisi
                preventivi non sono critici. Una notifica critica che non era
                necessaria è il modo più rapido per farsi disattivare le
                notifiche — comprese quelle della Protezione Civile regionale.
              </AlertDescription>
            </Alert>

            <ToggleRow
              id="critical"
              label="Invia come notifica critica"
              hint={
                !draft.push
                  ? 'Prima accendi «Notifica i cittadini»: senza notifica non c’è niente da rendere critico.'
                  : draft.critical
                    ? 'Suonerà a volume pieno anche sui telefoni in silenzioso. Te lo ricordo di nuovo prima di pubblicare.'
                    : 'Notifica normale: rispetta silenzioso e «Non disturbare».'
              }
              checked={draft.push && draft.critical}
              disabled={!draft.push}
              onChange={(v) => set('critical', v)}
            />
          </CardContent>
        </Card>
      )}

      {/* Fuori da ogni card: fino al 04/09/2026 stava in coda a «Notifica»,
          ma il box critico va DOPO quella card (dipende dal suo interruttore)
          e un «Continua» sopra la scelta più pesante della form si preme
          senza averla vista. */}
      <div className="flex flex-col gap-2">
        <Button type="submit" size="lg" disabled={check.isPending}>
          {check.isPending ? 'Controllo…' : 'Continua'}
        </Button>
        <p className="text-[13px] text-ink3">
          Il passo successivo mostra l’anteprima e quanti dispositivi la
          riceveranno. Non si pubblica ancora niente.
        </p>
      </div>
    </form>
  );
}

/* -------------------------------------------------------------------------- */

/** Etichetta, campo, contatore e messaggio d'errore — sempre nello stesso ordine. */
function Field({
  id,
  label,
  hint,
  error,
  counter,
  children,
}: {
  id: string;
  label: string;
  hint?: React.ReactNode;
  error?: string;
  counter?: [number, number];
  children: React.ReactNode;
}) {
  const show = counter && counter[0] > counter[1] * 0.75;
  const over = counter && counter[0] > counter[1];
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <Label htmlFor={id}>{label}</Label>
        {show && (
          <span className={cn('tnum text-[12px]', over ? 'text-rossa' : 'text-ink4')}>
            {counter[0]}/{counter[1]}
          </span>
        )}
      </div>
      {children}
      {(error || hint) && (
        <p
          {...(error ? { role: 'alert' as const, id: `${id}-error` } : {})}
          className={cn(
            'text-[13px] leading-snug',
            error ? 'font-medium text-rossa' : 'text-ink3',
          )}
        >
          {error ?? hint}
        </p>
      )}
    </div>
  );
}

/** Un interruttore con la sua conseguenza scritta accanto, non altrove. */
function ToggleRow({
  id,
  label,
  hint,
  checked,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  hint: React.ReactNode;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-6 rounded-lg border p-4',
        disabled && 'opacity-60',
      )}
    >
      <div className="min-w-0">
        <Label htmlFor={id} className="text-[15px]">
          {label}
        </Label>
        <p className="mt-1 text-[13px] leading-snug text-ink3">{hint}</p>
      </div>
      <Switch id={id} checked={checked} disabled={disabled} onCheckedChange={onChange} />
    </div>
  );
}
